import { AuthService } from "./auth.service";

export const SessionService = {
  // Session timeout (30 minutes in milliseconds)
  SESSION_TIMEOUT: 720 * 60 * 1000,

  // Warning timeout (5 minutes before session expires)
  WARNING_TIMEOUT: 715 * 60 * 1000,

  sessionTimer: null,
  warningTimer: null,

  /**
   * Initialize session management
   */
  init() {
    this.startSessionTimer();
    this.setupActivityListeners();
    this.checkExistingSession();
  },

  /**
   * Start session timeout timer
   */
  startSessionTimer() {
    this.clearTimers();

    // Set warning timer
    this.warningTimer = setTimeout(() => {
      this.showSessionWarning();
    }, this.WARNING_TIMEOUT);

    // Set session timeout timer
    this.sessionTimer = setTimeout(() => {
      this.handleSessionTimeout();
    }, this.SESSION_TIMEOUT);

    // Store session start time
    localStorage.setItem("session_start", Date.now().toString());
  },

  /**
   * Reset session timer on user activity
   */
  resetSessionTimer() {
    if (this.isAuthenticated()) {
      this.startSessionTimer();
      this.hideSessionWarning();
    }
  },

  /**
   * Clear all timers
   */
  clearTimers() {
    if (this.sessionTimer) {
      clearTimeout(this.sessionTimer);
      this.sessionTimer = null;
    }
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
  },

  /**
   * Setup activity listeners
   */
  setupActivityListeners() {
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];

    const resetTimer = () => {
      this.resetSessionTimer();
    };

    events.forEach((event) => {
      document.addEventListener(event, resetTimer, true);
    });
  },

  /**
   * Check existing session validity
   */
  checkExistingSession() {
    const sessionStart = localStorage.getItem("session_start");
    const token =
      localStorage.getItem("auth_token") ||
      localStorage.getItem("access_token");

    if (sessionStart && token) {
      const elapsed = Date.now() - parseInt(sessionStart);

      if (elapsed > this.SESSION_TIMEOUT) {
        this.handleSessionTimeout();
      } else {
        // Calculate remaining time and set appropriate timers
        const remaining = this.SESSION_TIMEOUT - elapsed;
        const warningRemaining = this.WARNING_TIMEOUT - elapsed;

        if (warningRemaining > 0) {
          this.warningTimer = setTimeout(() => {
            this.showSessionWarning();
          }, warningRemaining);
        } else {
          this.showSessionWarning();
        }

        this.sessionTimer = setTimeout(() => {
          this.handleSessionTimeout();
        }, remaining);
      }
    }
  },

  /**
   * Show session warning
   */
  showSessionWarning() {
    // Create warning modal/notification
    const warning = document.createElement("div");
    warning.id = "session-warning";
    warning.className = "session-warning-modal";
    warning.innerHTML = `
      <div class="session-warning-content">
        <h3>Session Expiring Soon</h3>
        <p>Your session will expire in 5 minutes due to inactivity.</p>
        <div class="session-warning-buttons">
          <button id="extend-session" class="btn btn-primary">Extend Session</button>
          <button id="logout-now" class="btn btn-secondary">Logout Now</button>
        </div>
      </div>
    `;

    document.body.appendChild(warning);

    // Add event listeners
    document.getElementById("extend-session").addEventListener("click", () => {
      this.extendSession();
    });

    document.getElementById("logout-now").addEventListener("click", () => {
      this.handleSessionTimeout();
    });
  },

  /**
   * Hide session warning
   */
  hideSessionWarning() {
    const warning = document.getElementById("session-warning");
    if (warning) {
      warning.remove();
    }
  },

  /**
   * Extend session
   */
  async extendSession() {
    try {
      // Verify token is still valid
      await AuthService.verifyToken();
      this.resetSessionTimer();
      this.hideSessionWarning();
    } catch (error) {
      this.handleSessionTimeout();
    }
  },

  /**
   * Handle session timeout
   */
  handleSessionTimeout() {
    this.clearTimers();
    this.hideSessionWarning();
    this.clearSessionData();

    // Redirect to login
    window.location.href = "/auth/login";
  },

  /**
   * Clear session data
   */
  clearSessionData() {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("access_token"); // Remove API compatibility token too
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("session_start");
    localStorage.removeItem("user_data");
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return !!(
      localStorage.getItem("auth_token") || localStorage.getItem("access_token")
    );
  },

  /**
   * Handle security events (password change, etc.)
   */
  invalidateAllSessions() {
    this.clearSessionData();
    this.clearTimers();

    // Force logout and redirect
    window.location.href = "/auth/login?session_invalidated=true";
  },

  /**
   * Get session information
   */
  getSessionInfo() {
    const sessionStart = localStorage.getItem("session_start");
    if (!sessionStart) return null;

    const elapsed = Date.now() - parseInt(sessionStart);
    const remaining = this.SESSION_TIMEOUT - elapsed;

    return {
      startTime: new Date(parseInt(sessionStart)),
      elapsed: elapsed,
      remaining: Math.max(0, remaining),
      isExpired: remaining <= 0,
    };
  },
};
